import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Check, Phone, Calendar, Clock, MessageSquare, TrendingUp, Zap, ArrowRight, BarChart3, Shield, Sparkles, CheckCircle, Users, Bell, Star, LayoutGrid, Database, Lock, Command, Globe, Sliders, MonitorPlay, FileText, Activity, Settings, Layers, Target, Mail, Plane, History } from 'lucide-react';

// Import Assets
import amandaImg from '../assets/amanda.png';

const HygieneRecallPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 0.5; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        .animate-pulse-ring {
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Stephanie, Your AI Recall Agent</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold leading-tight tracking-tight">
                <span className="text-gray-900">The #1 AI Hygiene</span><br />
                <span className="text-blue-600">Recall Agent</span>
              </h1>
              
              <p className="text-base text-gray-700 leading-relaxed max-w-lg">
                Stephanie proactively contacts overdue patients to fill your hygiene schedule. She runs intelligent campaigns, handles objections, and books appointments directly into Asha PMS after migration.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <button className="px-6 py-2.5 rounded-lg font-medium text-sm transition-all bg-white text-gray-900 hover:bg-gray-50 border border-gray-300 shadow-sm">
                  Get a demo
                </button>
                <button className="px-6 py-2.5 rounded-lg font-medium text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md">
                  See pricing
                </button>
              </div>
            </div>
            
            {/* Right: Interactive AI Visual */}
            <div className="relative flex items-center justify-center h-[500px]">
              {/* Main Profile with Card */}
              <div className="relative">
                {/* Main Image Card with Gradient Glow */}
                <div className="relative p-1 rounded-3xl bg-gradient-to-br from-blue-300 via-blue-400 to-purple-400 shadow-xl">
                  <div className="relative bg-gradient-to-br from-blue-50 to-purple-50 rounded-[22px] overflow-hidden w-72 h-96">
                    <img
                      src="/assets/stephanie.png"
                      alt="Stephanie AI Recall Agent"
                      className="w-full h-full object-cover opacity-95"
                    />
                  
                  {/* Glowing circle indicator on ear */}
                  <div className="absolute top-32 right-12">
                    <div className="relative">
                      <div className="w-8 h-8 rounded-full bg-cyan-300 animate-pulse"></div>
                      <div className="absolute inset-0 w-8 h-8 rounded-full bg-cyan-200 animate-ping"></div>
                    </div>
                  </div>
                  
                  {/* Audio waveform at bottom */}
                  <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-2xl px-6 py-3 shadow-md border border-gray-200">
                    <div className="flex items-center gap-1">
                      {[4, 8, 6, 10, 5, 9, 4, 7, 11, 6, 8, 5].map((height, i) => (
                        <div 
                          key={i} 
                          className="w-1 bg-blue-500 rounded-full"
                          style={{ height: `${height * 2}px` }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
                </div>
                
                {/* Speech bubble - top left */}
                <div className="absolute -top-4 -left-16 bg-white rounded-2xl p-4 shadow-md w-60 border border-gray-200">
                  <p className="text-xs text-gray-600 leading-relaxed">Hi Sarah, it's Stephanie from Dr. Smith's office. It's been 6 months since your last cleaning. Can we get you on the schedule?</p>
                  {/* Tail */}
                  <div className="absolute bottom-6 right-0 w-3 h-3 bg-white transform rotate-45 translate-x-1.5 border-r border-b border-gray-200"></div>
                </div>
                
                {/* Response Assistant badge - bottom left */}
                <div className="absolute bottom-24 -left-12 bg-white rounded-xl px-3 py-2 shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Target className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-xs font-medium text-gray-600">Smart Targeting</span>
                  </div>
                </div>
                
                {/* Stats card - top right */}
                <div className="absolute top-4 -right-16 bg-white rounded-xl p-3 shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-blue-500" />
                    <span className="text-xs font-medium text-gray-700">Daily Bookings: 12</span>
                  </div>
                </div>
                
                {/* Circular stats - bottom right */}
                <div className="absolute -bottom-8 -right-20 bg-white rounded-2xl p-4 shadow-md border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="relative w-16 h-16">
                      {/* Semi-circle graph */}
                      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="3"
                        />
                        <path
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831"
                          fill="none"
                          stroke="#3b82f6"
                          strokeWidth="3"
                          strokeDasharray="85, 100"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xs font-bold text-gray-700">85%</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="w-2 h-2 rounded-full bg-green-400"></div>
                        <span className="text-xs text-gray-500">Converted</span>
                      </div>
                      <p className="text-xs font-semibold text-gray-700">Booking Rate</p>
                      <p className="text-xs text-gray-400">High Engagement</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section - Text Left, Audio Right */}
      <section className="py-20 px-6 bg-white overflow-hidden border-b border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="space-y-8 relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium border border-blue-100 mb-4 uppercase tracking-wide">
                  Beyond Basic Reminders
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  Why AI Recall Beats <br/>
                  <span className="text-blue-600">Standard SMS Reminders</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Standard SMS reminders only reach a small subset of patients. Stephanie runs intelligent campaigns using both voice and SMS, having real conversations to educate patients on hygiene and schedule them into the correct operatory based on their preferences and your rules.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed mt-4">
                  She understands sentiment—if a patient asks to be called back in a few weeks, she follows up at the right time. Unlike manual staff calls where 75% go unanswered, our AI tracks every attempt and persists effectively without burning out your team on low-leverage work.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <button className="px-8 py-4 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5">
                  Book a Demo
                </button>
                <button className="px-8 py-4 rounded-xl font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all">
                  View Pricing
                </button>
              </div>

            </div>

            {/* Right: Audio Demo Player */}
            <div className="relative">
              {/* Background Decorative Elements */}
              <div className="absolute -top-10 -right-10 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
              <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
              
              <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#0f172a] p-6 text-white border-b border-gray-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold">Watch Stephanie In Action</h3>
                      <p className="text-gray-400 text-sm">See how she navigates objections</p>
                    </div>
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <MonitorPlay className="w-5 h-5 text-blue-400" />
                    </div>
                  </div>
                </div>
                
                <div className="aspect-video w-full bg-slate-900 relative group">
                    <iframe 
                        src="https://www.loom.com/embed/09fe838559954860bfc461e6d88bd3a2" 
                        frameBorder="0" 
                        webkitallowfullscreen="true" 
                        mozallowfullscreen="true" 
                        allowFullScreen 
                        className="absolute top-0 left-0 w-full h-full"
                    ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW SECTION: The Only AI That Truly Understands Dentistry (VoiceStack Style) */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-16 max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    Smart Campaigns That <br/>
                    <span className="text-blue-600">Actually Convert</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                    Stephanie isn't a robotic dialer. She's an intelligent agent that analyzes your patient base, prioritizes outreach, and has real conversations to bring patients back.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Card 1: Multi-Channel Engagement */}
                <div className="flex flex-col">
                    {/* Visual Area - Glassmorphic on Gradient */}
                    <div className="bg-gradient-to-br from-blue-100 via-purple-100 to-blue-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         {/* Background Orbs */}
                         <div className="absolute top-0 right-0 w-64 h-64 bg-blue-300/30 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
                         <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-300/30 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>
                         
                         {/* Glass Card Container */}
                         <div className="relative w-full max-w-md">
                            <div className="bg-white/60 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-xl w-full">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Outreach Sequence</span>
                                    <span className="bg-blue-100 text-blue-700 text-[10px] px-2 py-1 rounded-full font-bold">Active</span>
                                </div>
                                <div className="space-y-4 relative">
                                    <div className="absolute left-3.5 top-2 bottom-2 w-0.5 bg-gray-200/50"></div>
                                    
                                    {/* Step 1 */}
                                    <div className="relative flex items-center gap-3 opacity-50">
                                        <div className="w-7 h-7 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center z-10 flex-shrink-0">
                                            <Phone className="w-3 h-3 text-gray-400" />
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            <span className="font-bold block">Day 1 - 5:30 PM</span>
                                            Voice Call (No Answer)
                                        </div>
                                    </div>

                                    {/* Step 2 */}
                                    <div className="relative flex items-center gap-3 opacity-70">
                                        <div className="w-7 h-7 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center z-10 flex-shrink-0">
                                            <MessageSquare className="w-3 h-3 text-blue-500" />
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            <span className="font-bold block">Day 2 - 10:00 AM</span>
                                            SMS Follow-up Sent
                                        </div>
                                    </div>

                                    {/* Step 3 */}
                                    <div className="relative flex items-center gap-3">
                                        <div className="w-7 h-7 rounded-full bg-green-100 border border-green-200 flex items-center justify-center z-10 ring-2 ring-green-50 flex-shrink-0">
                                            <Phone className="w-3 h-3 text-green-600" />
                                        </div>
                                        <div className="text-xs text-gray-800 bg-white p-2 rounded-lg shadow-sm border border-gray-100 w-full">
                                            <span className="font-bold block text-green-600">Day 4 - 4:15 PM • Connected</span>
                                            "Call me back next week..."
                                            <div className="mt-1 flex gap-1 flex-wrap">
                                                <span className="bg-yellow-50 text-yellow-700 text-[9px] px-1.5 py-0.5 rounded border border-yellow-100">Sentiment: Busy</span>
                                                <span className="bg-blue-50 text-blue-700 text-[9px] px-1.5 py-0.5 rounded border border-blue-100">Action: Snooze 7 Days</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                         </div>
                    </div>

                    {/* Text Content */}
                    <div>
                        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <Target className="w-4 h-4" />
                             VOICE + SMS + SENTIMENT
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">Smart Campaign Engine</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Stephanie uses both AI Voice and SMS to reach patients. She tracks sentiment and makes multiple attempts over time, achieving far higher conversion rates than standard one-off SMS blasts.
                        </p>
                    </div>
                </div>

                {/* Card 2: Education & Conversion */}
                <div className="flex flex-col">
                    {/* Visual Area - Glassmorphic on Gradient */}
                    <div className="bg-gradient-to-br from-indigo-100 via-blue-100 to-indigo-50 rounded-[2.5rem] p-8 h-[400px] relative overflow-hidden mb-6 flex items-center justify-center shadow-sm">
                         {/* Background Orbs */}
                         <div className="absolute top-10 left-10 w-40 h-40 bg-purple-300/30 rounded-full blur-3xl"></div>
                         <div className="absolute bottom-10 right-10 w-40 h-40 bg-blue-300/30 rounded-full blur-3xl"></div>
                         
                        <div className="relative w-full max-w-md z-10">
                            {/* Call Interface */}
                            <div className="bg-white/60 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-xl w-full flex flex-col items-center text-center relative overflow-hidden">
                                <div className="w-20 h-20 rounded-full p-1 bg-gradient-to-br from-blue-100 to-indigo-100 mb-4 relative z-10">
                                    <img src="/assets/stephanie.png" alt="Stephanie" className="w-full h-full rounded-full object-cover" />
                                    <div className="absolute bottom-0 right-0 bg-green-500 p-1.5 rounded-full border-2 border-white">
                                        <Phone className="w-3 h-3 text-white" />
                                    </div>
                                </div>
                                
                                <div className="space-y-1 mb-4 relative z-10">
                                    <h4 className="font-bold text-gray-900">Stephanie</h4>
                                    <p className="text-xs text-blue-600 font-medium">Educating Patient...</p>
                                </div>

                                {/* Waveform */}
                                <div className="flex items-center justify-center gap-1 h-6 mb-5 relative z-10">
                                    {[3, 5, 4, 7, 6, 4, 3, 5, 4, 6, 3, 4].map((h, i) => (
                                        <div key={i} className="w-1 bg-blue-500 rounded-full animate-pulse" style={{ height: `${h * 4}px`, animationDelay: `${i * 0.1}s` }}></div>
                                    ))}
                                </div>

                                {/* Live Insights */}
                                <div className="w-full space-y-2 relative z-10">
                                    <div className="bg-white p-2.5 rounded-xl shadow-sm border border-green-100 flex items-center gap-2 animate-float" style={{ animationDelay: '0s' }}>
                                        <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                                        <span className="text-xs font-medium text-gray-700">Explaining "Perio Maintenance"</span>
                                    </div>
                                    <div className="bg-white p-2.5 rounded-xl shadow-sm border border-blue-100 flex items-center gap-2 animate-float" style={{ animationDelay: '1.5s' }}>
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                        <span className="text-xs font-medium text-gray-700">Highlighting Unused Benefits</span>
                                    </div>
                                </div>
                                
                                {/* Abstract Bg */}
                                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent to-blue-50/50 z-0"></div>
                            </div>
                        </div>
                    </div>

                    {/* Text Content */}
                    <div>
                        <div className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                             <MessageSquare className="w-4 h-4" />
                             INTELLIGENT VOICE CONVERSATIONS
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2.5">Educates & Converts</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Voice converts better than text. Stephanie has real, educational conversations about oral health, explaining <em>why</em> the visit is important, which drives patients to book.
                        </p>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* REFINED SECTION: Bento Grid */}
      <section className="py-24 px-6 bg-white border-t border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-16 max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              The World's Most Sophisticated <br />
              <span className="text-blue-600">Recall System</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Automate your patient retention with tools designed specifically for dental hygiene recall.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1 - Intelligent Lists (Span 2) */}
            <div className="md:col-span-2 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10 max-w-lg">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-blue-600">
                  <Database className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Intelligent Patient Lists</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Stephanie automatically segments your patient base. She knows who is due for a prophy, who needs perio maintenance, and who has unscheduled treatment plans.
                </p>
              </div>
              
              {/* List Graphic Overlay */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[45%] h-full hidden md:flex items-center justify-center p-6">
                 <div className="relative w-full max-w-xs space-y-3">
                     <div className="glass-card bg-white/80 p-3 rounded-xl shadow-md border border-white/50 flex justify-between items-center transform translate-x-4">
                        <span className="text-xs font-bold text-gray-700">Due for Prophy</span>
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">342 Patients</span>
                     </div>
                     <div className="glass-card bg-white/90 p-3 rounded-xl shadow-lg border border-white/60 flex justify-between items-center z-10 relative">
                        <span className="text-xs font-bold text-gray-700">Unscheduled Treatment</span>
                        <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">128 Patients</span>
                     </div>
                     <div className="glass-card bg-white/80 p-3 rounded-xl shadow-md border border-white/50 flex justify-between items-center transform translate-x-4">
                        <span className="text-xs font-bold text-gray-700">Insurance Expiring</span>
                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">56 Patients</span>
                     </div>
                 </div>
              </div>
              <div className="absolute right-0 bottom-0 w-1/2 h-full bg-gradient-to-l from-blue-50/50 to-transparent -z-10"></div>
            </div>

            {/* Card 2 - Deep Integration (Span 1) */}
            <div className="md:col-span-1 bg-white rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600">
                  <Calendar className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Seamless Data Migration</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  We migrate Dentrix and Eaglesoft practices onto Asha so Stephanie can work from your live schedule on day one.
                </p>
                
                {/* Real PMS Logos */}
                <div className="grid grid-cols-2 gap-4 items-center opacity-80">
                    <div className="p-2 bg-gray-50 rounded-lg flex items-center justify-center h-12">
                        <img src="https://framerusercontent.com/images/FK2PxMGUliSNEptVBpWhWGjhW4.png" alt="Dentrix" className="max-h-8 object-contain" />
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg flex items-center justify-center h-12">
                         <img src="https://framerusercontent.com/images/JN5q8qzSCZ7K4LwF01UYdhkXhmI.png" alt="Eaglesoft" className="max-h-8 object-contain" />
                    </div>
                </div>
              </div>
            </div>

            {/* Card 3 - Customization (Span 1) */}
            <div className="md:col-span-1 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-blue-600">
                  <Sliders className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Custom Scripts</h3>
                <p className="text-gray-600 leading-relaxed">
                  Tailor the message. Choose from friendly reminders, urgent notices, or benefit-focused scripts.
                </p>
              </div>
            </div>

            {/* Card 4 - Full Team (Span 1) */}
             <div className="md:col-span-1 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-purple-600">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Team Hand-off</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Stephanie collaborates with Amy (Scheduling) and Amanda (Insurance) for complex cases.
                </p>
                 <div className="flex -space-x-3">
                    <img src="/assets/amy.png" className="w-10 h-10 rounded-full border-2 border-white shadow-sm" alt="Amy" />
                    <img src="/assets/amanda.png" className="w-10 h-10 rounded-full border-2 border-white shadow-sm" alt="Amanda" />
                    <div className="w-10 h-10 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs text-gray-600 font-bold">+12</div>
                 </div>
              </div>
            </div>

            {/* Card 5 - ROI Dashboard (Span 1) */}
             <div className="md:col-span-1 bg-slate-900 rounded-[2rem] p-8 overflow-hidden relative group transition-all hover:shadow-xl text-white">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center mb-6 text-blue-400">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">ROI Tracking</h3>
                <p className="text-slate-400 leading-relaxed">
                  Track exactly how much revenue Stephanie generates from recalled patients.
                </p>
              </div>
               <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:16px_16px]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW SECTION: Patient Memory */}
      <section className="py-24 px-6 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>

        <div className="container mx-auto max-w-7xl relative z-10">
            <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    A Memory That <span className="text-blue-400">Never Fades</span>
                </h2>
                <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                    Human staff forget. Stephanie doesn't. She tracks conversation context across weeks or months to call back at the perfect moment.
                </p>
            </div>

            <div className="max-w-4xl mx-auto">
                <div className="relative">
                    {/* Timeline Line */}
                    <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-700"></div>

                    <div className="space-y-12">
                        {/* Event 1 */}
                        <div className="relative flex gap-8 items-start group">
                            <div className="w-16 h-16 rounded-full bg-slate-800 border-4 border-slate-900 flex items-center justify-center z-10 shadow-xl group-hover:scale-110 transition-transform">
                                <Phone className="w-6 h-6 text-blue-400" />
                            </div>
                            <div className="flex-1 bg-slate-800/50 rounded-2xl p-6 border border-slate-700 hover:border-blue-500/30 transition-colors">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-lg">Initial Outreach</h3>
                                    <span className="text-xs font-mono text-slate-400">Oct 12</span>
                                </div>
                                <div className="text-slate-300 italic mb-3">"I'm actually heading to Europe for a few weeks. Can you try me when I get back?"</div>
                                <div className="inline-flex items-center gap-2 text-xs font-bold text-yellow-400 bg-yellow-400/10 px-3 py-1 rounded-full">
                                    <Plane className="w-3 h-3" />
                                    Detected: Travel (3 Weeks)
                                </div>
                            </div>
                        </div>

                        {/* Event 2 */}
                        <div className="relative flex gap-8 items-start group">
                            <div className="w-16 h-16 rounded-full bg-slate-800 border-4 border-slate-900 flex items-center justify-center z-10 shadow-xl group-hover:scale-110 transition-transform">
                                <History className="w-6 h-6 text-purple-400" />
                            </div>
                            <div className="flex-1 bg-slate-800/50 rounded-2xl p-6 border border-slate-700 hover:border-purple-500/30 transition-colors">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-lg">Memory Triggered</h3>
                                    <span className="text-xs font-mono text-slate-400">Nov 2</span>
                                </div>
                                <p className="text-sm text-slate-400">
                                    Stephanie waits exactly 21 days. She doesn't call during the trip. She wakes up the task automatically upon the patient's return.
                                </p>
                            </div>
                        </div>

                        {/* Event 3 */}
                        <div className="relative flex gap-8 items-start group">
                            <div className="w-16 h-16 rounded-full bg-green-500 border-4 border-slate-900 flex items-center justify-center z-10 shadow-xl group-hover:scale-110 transition-transform">
                                <CheckCircle className="w-8 h-8 text-white" />
                            </div>
                            <div className="flex-1 bg-gradient-to-r from-green-900/20 to-slate-800/50 rounded-2xl p-6 border border-green-500/30">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-lg text-white">Successful Booking</h3>
                                    <span className="text-xs font-mono text-slate-400">Nov 2, 10:00 AM</span>
                                </div>
                                <div className="text-slate-300 italic mb-3">"Hi! Stephanie here. Welcome back from your trip! I hope Europe was amazing. Ready to get that cleaning scheduled?"</div>
                                <div className="inline-flex items-center gap-2 text-xs font-bold text-green-400 bg-green-400/10 px-3 py-1 rounded-full">
                                    <Zap className="w-3 h-3" />
                                    Context-Aware Conversion
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* Why AI Beats Manual Recall */}
      <section className="py-14 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            
            {/* Left: Content */}
            <div>
              <p className="text-xs text-blue-600 font-bold uppercase tracking-wider mb-2.5">SUPERIOR PERFORMANCE</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-3 leading-tight">
                Objections that would <br/>
                <span className="text-blue-600">stop your front desk...</span>
              </h2>

              <p className="text-base text-gray-600 mb-6 leading-relaxed">
                Manual recall calls are exhausting and often result in voicemail. Stephanie persists without fatigue, handles objections with perfect scripts, and works evenings/weekends when patients are actually available.
              </p>

              <div className="space-y-4">

                <div className="flex gap-3.5">
                  <div className="flex-shrink-0 w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1.5">"I'm too busy right now"</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Stephanie: "No problem. I can call you back at a better time, or would you prefer I send a text link to schedule yourself?"
                    </p>
                  </div>
                </div>

                <div className="flex gap-3.5">
                  <div className="flex-shrink-0 w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1.5">"Is it covered by insurance?"</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Stephanie checks with Amanda (Insurance Agent) instantly: "Yes, you have 2 cleanings remaining this year with Delta Dental."
                    </p>
                  </div>
                </div>

                <div className="flex gap-3.5">
                  <div className="flex-shrink-0 w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Zap className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1.5">Instant Action</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Stephanie books the appointment immediately during the call. No "let me check the schedule and call you back."
                    </p>
                  </div>
                </div>
              </div>

            </div>

            {/* Right: Interactive Dashboard Card */}
            <div className="relative h-[500px] bg-gray-50/50 rounded-3xl overflow-hidden border border-gray-200 shadow-xl flex items-center justify-center p-8">
              {/* Decorative Background Elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100/50 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-green-100/50 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
              
              <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col relative z-10 transform transition-transform hover:scale-[1.02] duration-500">
                {/* Header */}
                <div className="bg-white p-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="font-semibold text-sm text-gray-700">Live Call</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
                        <Phone className="w-3 h-3 text-blue-600" />
                        <span className="text-[10px] font-bold text-blue-700 tracking-wide">OUTBOUND</span>
                    </div>
                </div>
                
                {/* Transcript Area */}
                <div className="flex-1 p-5 space-y-6 bg-gray-50/30">
                    {/* AI Message */}
                    <div className="flex gap-4 flex-row-reverse">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden ring-2 ring-blue-50">
                            <img src="/assets/stephanie.png" alt="Stephanie" className="w-full h-full object-cover" />
                        </div>
                        <div className="space-y-1">
                            <div className="bg-blue-600 p-3.5 rounded-2xl rounded-tr-none shadow-md text-sm text-white leading-relaxed max-w-[240px]">
                                Hi John, Dr. Smith noticed it's been a while. Gum health is really important for your overall health. Can we schedule a quick cleaning?
                            </div>
                            <div className="flex items-center justify-end gap-1.5 pr-1">
                                <span className="text-[10px] text-gray-400">10:42 AM</span>
                            </div>
                        </div>
                    </div>

                    {/* User Message */}
                    <div className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-500">J</div>
                        <div className="space-y-1">
                            <div className="bg-white p-3.5 rounded-2xl rounded-tl-none shadow-sm border border-gray-100 text-sm text-gray-700 leading-relaxed max-w-[240px]">
                                I'm really swamped at work lately. I don't know when I can come in.
                            </div>
                            <span className="text-[10px] text-gray-400 pl-1">10:43 AM</span>
                        </div>
                    </div>

                    {/* AI Message */}
                    <div className="flex gap-4 flex-row-reverse">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden ring-2 ring-blue-50">
                            <img src="/assets/stephanie.png" alt="Stephanie" className="w-full h-full object-cover" />
                        </div>
                        <div className="space-y-1">
                            <div className="bg-blue-600 p-3.5 rounded-2xl rounded-tr-none shadow-md text-sm text-white leading-relaxed max-w-[240px]">
                                I understand. We actually have some late afternoon spots or Saturday mornings available. Would a Saturday at 10 AM work better for your schedule?
                            </div>
                            <div className="flex items-center justify-end gap-1.5 pr-1">
                                <span className="text-[10px] text-gray-400">10:43 AM</span>
                                <CheckCircle className="w-3 h-3 text-blue-500" />
                            </div>
                        </div>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Dentina Style */}
      <section className="py-14 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-700 shadow-xl border border-blue-500/50">
            <div className="grid lg:grid-cols-2 gap-0 items-center">
              {/* Left: Content */}
              <div className="p-10 lg:p-12 relative z-10">
                <p className="text-xs font-bold text-blue-100 tracking-wider mb-3 uppercase">
                  STOP LOSING REVENUE
                </p>
                
                <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white leading-tight">
                  Advanced Dental Health reactivated 8 long-lost patients in just 30 days.
                </h2>
                
                <p className="text-base text-blue-100 mb-6 leading-relaxed max-w-xl">
                  Don't let your patients slip through the cracks. Stephanie ensures your hygiene schedule stays full, just like she did for Advanced Dental Health, generating thousands in immediate production.
                </p>
                
                <button className="group px-7 py-3 rounded-xl font-bold text-sm transition-all bg-white text-blue-600 hover:bg-blue-50 shadow-lg hover:shadow-xl">
                  <span className="flex items-center gap-2">
                    Book a Demo
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
              </div>

              {/* Right: Graphic Composition */}
              <div className="relative h-full min-h-[400px] flex items-center justify-center p-8 overflow-hidden">
                {/* Background decorative elements */}
                <div className="absolute inset-0">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white/10 rounded-full"></div>
                </div>

                {/* Main Card */}
                <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 w-72 transform rotate-2 hover:rotate-0 transition-transform duration-500">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Live Agent</span>
                    </div>
                    <span className="text-xs font-mono text-gray-400">01:15</span>
                  </div>

                  {/* Avatar Area */}
                  <div className="flex flex-col items-center mb-6">
                    <div className="relative mb-3">
                      <div className="w-20 h-20 rounded-full p-1 bg-gradient-to-br from-blue-100 to-indigo-100">
                        <img
                          src="/assets/stephanie.png"
                          alt="Stephanie"
                          className="w-full h-full rounded-full object-cover"
                        />
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm">
                        <div className="bg-blue-600 rounded-full p-1">
                          <Phone className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Stephanie</h3>
                    <p className="text-sm text-blue-600 font-medium">Recall Specialist</p>
                  </div>

                  {/* Waveform Visualization */}
                  <div className="flex items-center justify-center gap-1 h-8 mb-4">
                    {[1, 2, 3, 2, 4, 3, 5, 4, 2, 3, 1, 2].map((h, i) => (
                      <div
                        key={i}
                        className="w-1 bg-blue-500 rounded-full animate-pulse"
                        style={{ height: `${h * 20}%`, animationDelay: `${i * 0.1}s` }}
                      ></div>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg py-2 text-center">
                      <span className="text-xs font-semibold text-gray-600">Recall</span>
                    </div>
                    <div className="bg-gray-50 rounded-lg py-2 text-center">
                      <span className="text-xs font-semibold text-gray-600">Education</span>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute top-20 right-10 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform rotate-6 animate-float">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                </div>
                <div className="absolute bottom-20 left-10 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform -rotate-6 animate-float" style={{ animationDelay: '1s' }}>
                  <Calendar className="w-5 h-5 text-indigo-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HygieneRecallPage;
